import { useMemo, useState } from 'react';
import { Button, Select, MenuItem } from '@mui/material';

import {
    DataTable,
    Loading,
    ErrorMessage,
    type TableColumn,
    ActivityLogIcon,
    SimpleTextField,
    Label,
} from '@/src/ui';
import type { StudentHistoryDTO, StudentExamAttemptDTO } from '../models/studentHistoryDTOts';
import { ReportCellStyle, ReportHeaderStyle, Constants } from '@/src/utils';
import StudentAttemptsModal from './StudentAttemptsModal';

interface StudentHistoryTableProps {
    data: StudentHistoryDTO[];
    isLoading: boolean;
    mode: 'normal' | 'student-limited';
    error?: string;
}

export default function StudentHistoryTable({
    data,
    isLoading,
    mode,
    error,
}: StudentHistoryTableProps) {
    const [searchTerm, setSearchTerm] = useState('');

    const tableData = useMemo(() => {
        const list = data ?? [];
        if (!searchTerm.trim()) return list;
        return list.filter((d) => d.studentId.toString().includes(searchTerm.trim()));
    }, [data, searchTerm]);

    const [selectedStudent, setSelectedStudent] = useState<StudentHistoryDTO | null>(null);
    const [selectedAttempt, setSelectedAttempt] = useState<StudentExamAttemptDTO | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    // track selected attempt per student
    const [selectedAttempts, setSelectedAttempts] = useState<Record<string, StudentExamAttemptDTO>>(
        {}
    );

    const columns = useMemo<TableColumn[]>(
        () => [
            {
                dataKey: 'studentName',
                header: 'Student Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examName',
                header: 'Assessment',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examType',
                header: 'Assessment Type',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'attemptCount',
                header: 'Attempts',
                align: 'center',
                formatter: (row: StudentHistoryDTO) => (row.attempts ?? []).length,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'score',
                header: 'Score',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: StudentHistoryDTO) => {
                    const attempts = row.attempts ?? [];
                    if (attempts.length === 0) return null;

                    const key = `${row.studentId}-${row.examId}`;
                    const selected = selectedAttempts[key];
                    const selectedId = selected?.id ?? attempts[attempts.length - 1].id;

                    const attempt = attempts.find((a) => a.id === selectedId);
                    if (!attempt || attempt.score == null) return '-';
                    return `${attempt.score}%`;
                },
            },
            {
                dataKey: 'category',
                header: 'Category',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: StudentHistoryDTO) => {
                    const attempts = row.attempts ?? [];
                    if (attempts.length === 0) return null;

                    const key = `${row.studentId}-${row.examId}`;
                    const selected = selectedAttempts[key];
                    const selectedId = selected?.id ?? attempts[attempts.length - 1].id;

                    const attempt = attempts.find((a) => a.id === selectedId);
                    if (!attempt || attempt.score == null) return '-';
                    return (
                        <Label color={Constants.bandColors[attempt.reportingCategory ?? '—']}>
                            {attempt.reportingCategory ?? '—'}
                        </Label>
                    );
                },
            },
            {
                dataKey: 'viewAttempt',
                header: 'View Attempt',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                excludeFromExport: true,
                formatter: (row: StudentHistoryDTO) => {
                    const attempts = row.attempts ?? [];
                    if (attempts.length === 0) return null;

                    const key = `${row.studentId}-${row.examId}`;
                    const selected = selectedAttempts[key];
                    const selectedId = selected?.id ?? attempts[attempts.length - 1].id;

                    return (
                        <div className="flex items-center justify-center gap-2">
                            <Select
                                size="small"
                                value={selectedId}
                                onChange={(e) => {
                                    const attempt = attempts.find(
                                        (a) => a.id === Number(e.target.value)
                                    );
                                    if (attempt) {
                                        setSelectedAttempts((prev) => ({
                                            ...prev,
                                            [key]: attempt,
                                        }));
                                    }
                                }}
                                sx={{
                                    minWidth: 120,
                                    fontSize: '0.875rem',
                                    height: 32,
                                    '& .MuiSelect-select': { py: 0.5 },
                                }}
                            >
                                {attempts.map((a) => (
                                    <MenuItem key={a.id} value={a.id}>
                                        Attempt {a.attemptNumber}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Button
                                size="small"
                                startIcon={<ActivityLogIcon />}
                                onClick={() => {
                                    const attempt =
                                        selectedAttempts[key] ?? attempts[attempts.length - 1];
                                    setSelectedStudent(row);
                                    setSelectedAttempt(attempt);
                                    setModalOpen(true);
                                }}
                            >
                                View
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [selectedAttempts]
    );
    const columnsFiltered = useMemo(() => {
        if (mode === 'student-limited') {
            return columns.filter((col) => col.dataKey !== 'studentName');
        }
        return columns;
    }, [columns, mode]);

    if (isLoading) return <Loading message="Loading Student History Report..." />;
    if (error) return <ErrorMessage error={error} />;

    return (
        <>
            {mode === 'normal' && (
                <>
                    <hr className="border-t border-gray-300 my-2" />
                    <br />
                    <div className="flex justify-end items-center mb-4">
                        <SimpleTextField
                            name="Search Student History"
                            label="Search by student ID..."
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: 220 }}
                        />
                    </div>
                </>
            )}

            <div className="flex flex-col gap-6 w-full overflow-auto">
                {tableData.length > 0 ? (
                    <DataTable
                        columns={columnsFiltered}
                        data={tableData}
                        tableName="Student Exam History"
                        rowsPerPageInitial={50}
                        enableExportCSV
                        alternatingRowColor
                        defaultSortColumn="studentName"
                        defaultSortDirection="asc"
                    />
                ) : (
                    <div className="w-full text-center py-12 text-gray-600">
                        No student exam history available.
                    </div>
                )}
            </div>

            {selectedStudent && selectedAttempt && (
                <StudentAttemptsModal
                    student={selectedStudent}
                    attempt={selectedAttempt}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </>
    );
}
